import { useEffect, useState } from 'react'
import { InfoTooltip } from '../../components/InfoTooltip'
import { Sparkline } from '../../components/Sparkline'
import { palette } from '@versports/theme'
import { getMyActiveKit, type MyActiveKit } from '../../api/physiology'
import { useLivePhysiology } from './useLivePhysiology'
import { DASHBOARD_INFO, METRIC_INFO, noDataLabel } from './metricInfo'
import { SignalsTrendChart } from './SignalsTrendChart'
import { toIndex100, type SignalsModel } from './signalsModel'

/**
 * Paneles de señales de `/data`. Hasta el 2026-09-07 este archivo era
 * `KnowPhysiology`, un dashboard entero (en vivo + KPIs + tendencia + última
 * sesión + tabla de TODAS las sesiones) montado como una sección más de la
 * página. La tabla de sesiones se fue a `SessionsSection` — la lista única de
 * la página — y la card de "última sesión con señal" desapareció por
 * redundante: es la primera fila de esa lista, expandible. Quedan dos piezas:
 *
 *   - `LiveSignalsCard`: sólo aparece si el jugador tiene el kit puesto AHORA.
 *   - `SignalsSummary`: KPIs + tendencia, para el bloque colapsable de detalle.
 *
 * Privacidad: todo pasa por endpoints que resuelven "el usuario logueado" del
 * lado del backend (`get_current_user_any` / `require_cookie_user`), nunca un
 * id de otro jugador.
 */

// ---------------------------------------------------------------------------
// En vivo
// ---------------------------------------------------------------------------

/**
 * Tiles en vivo mientras el jugador tiene un kit reclamado. Devuelve `null`
 * cuando no hay kit activo (el caso normal cuando mira la página): antes
 * ocupaba una card entera para decir "no tenés una sesión activa", que es
 * ruido en una página que ya era demasiado larga.
 */
export function LiveSignalsCard() {
  const [activeKit, setActiveKit] = useState<MyActiveKit | null | 'error'>(null)

  useEffect(() => {
    let cancelled = false
    // Poll liviano: el jugador puede ponerse/sacarse el kit mientras está
    // parado en esta pantalla (entra a KNOW antes de jugar, o vuelve
    // después). 5s es suficientemente responsivo sin martillar el backend.
    async function poll() {
      try {
        const kit = await getMyActiveKit()
        if (!cancelled) setActiveKit(kit)
      } catch {
        if (!cancelled) setActiveKit('error')
      }
    }
    void poll()
    const id = window.setInterval(poll, 5000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [])

  const kit =
    activeKit && activeKit !== 'error' && activeKit.rpi_id && activeKit.kit_id
      ? { rpiId: activeKit.rpi_id, kitId: activeKit.kit_id }
      : null

  const live = useLivePhysiology(kit)

  if (!kit) return null

  return (
    <div className="card border-primary/30">
      <div className="mb-md flex items-center justify-between">
        <div className="flex items-center gap-xs">
          <h2 className="m-0">En vivo</h2>
          <InfoTooltip label="En vivo">{DASHBOARD_INFO.section_live}</InfoTooltip>
        </div>
        <span className={live.connected ? 'tag-ok' : 'tag'}>
          {live.connected ? (live.registered ? 'Conectado' : 'Conectando…') : 'Reconectando…'}
        </span>
      </div>

      <div className="flex flex-col gap-md">
        {live.error && <div className="banner-err text-sm">{live.error}</div>}

        <div className="grid grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-4">
          <MetricTile
            label="HR"
            infoKey="hr"
            value={live.hr.heartrate !== null ? `${Math.round(live.hr.heartrate)} bpm` : null}
            reason={live.hr.heartrate === null ? 'esperando el primer latido del Polar H10' : null}
            series={live.series.hr}
            seriesColor={palette.danger}
          />
          <MetricTile
            label="VFC"
            infoKey="rmssd_live"
            value={
              live.metrics?.cardio.rmssd.rmssd_ms !== null && live.metrics?.cardio.rmssd.rmssd_ms !== undefined
                ? `${Math.round(live.metrics.cardio.rmssd.rmssd_ms)} ms`
                : null
            }
            reason={live.metrics?.cardio.rmssd.reason ?? (live.metrics ? null : 'esperando datos')}
          />
          <MetricTile
            label="SCL"
            infoKey="scl"
            value={
              live.metrics?.eda.scl.value !== null && live.metrics?.eda.scl.value !== undefined
                ? `${live.metrics.eda.scl.value.toFixed(2)} µS`
                : null
            }
            reason={live.metrics?.eda.scl.reason ?? (live.metrics ? null : 'esperando datos')}
            series={live.series.scl}
            seriesColor={palette.secondary}
          />
          <MetricTile
            label="dSCL/dt"
            infoKey="dscl_dt"
            value={
              live.metrics?.eda.dscl_dt.value_uS_per_s !== null &&
              live.metrics?.eda.dscl_dt.value_uS_per_s !== undefined
                ? `${live.metrics.eda.dscl_dt.value_uS_per_s.toFixed(3)} µS/s`
                : null
            }
            reason={live.metrics?.eda.dscl_dt.reason ?? (live.metrics ? null : 'esperando datos')}
          />
          <MetricTile
            label="Tasa SCR"
            infoKey="scr_rate"
            value={
              live.metrics?.eda.scr_rate.rate_per_min !== null &&
              live.metrics?.eda.scr_rate.rate_per_min !== undefined
                ? `${live.metrics.eda.scr_rate.rate_per_min.toFixed(1)} /min`
                : null
            }
            reason={live.metrics?.eda.scr_rate.reason ?? (live.metrics ? null : 'esperando datos')}
          />
          <MetricTile
            label="Autocontrol cardíaco"
            infoKey="r_live_cardio"
            value={
              live.metrics?.cardio.r_live_cardio !== null && live.metrics?.cardio.r_live_cardio !== undefined
                ? `${toIndex100(live.metrics.cardio.r_live_cardio)}`
                : null
            }
            reason={live.metrics?.cardio.reason ?? (live.metrics ? null : 'esperando datos')}
            series={live.series.rLiveCardio}
          />
          <MetricTile
            label="Autocontrol cardíaco + piel"
            infoKey="r_live_cardio_eda"
            value={
              live.metrics?.eda.r_live_cardio_eda !== null && live.metrics?.eda.r_live_cardio_eda !== undefined
                ? `${toIndex100(live.metrics.eda.r_live_cardio_eda)}`
                : null
            }
            reason={live.metrics?.eda.reason ?? (live.metrics ? null : 'esperando datos')}
          />
        </div>

        <p className="m-0 text-xs text-text-muted">
          Estimación continua sobre ventana móvil: no es la misma serie que el
          índice por ciclo de cada sesión, no se comparan.
        </p>
      </div>
    </div>
  )
}

function MetricTile({
  label,
  infoKey,
  value,
  reason,
  series,
  seriesColor,
}: {
  label: string
  infoKey: keyof typeof METRIC_INFO
  value: string | null
  reason?: string | null
  series?: number[]
  seriesColor?: string
}) {
  return (
    <div className="rounded-md border border-primary/20 bg-surface p-sm">
      <div className="flex items-center gap-xs text-xs uppercase tracking-wide text-text-muted">
        <span>{label}</span>
        <InfoTooltip label={label}>{METRIC_INFO[infoKey]}</InfoTooltip>
      </div>
      {value !== null ? (
        <>
          <div className="mt-xs font-display text-lg font-bold text-primary">{value}</div>
          {series && series.length > 1 && (
            <Sparkline values={series} color={seriesColor ?? palette.primary} />
          )}
        </>
      ) : (
        <div className="mt-xs text-xs italic text-text-muted">{noDataLabel(reason)}</div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Histórico resumido (va adentro del colapsable de detalle)
// ---------------------------------------------------------------------------

/**
 * KPIs de regulación + tendencia sesión a sesión. Cuando todavía no hay
 * ningún ciclo medido explica qué falta en vez de dibujar gráficos vacíos:
 * el caso más común no es "se está procesando" sino "jugaste sin el Polar".
 */
export function SignalsSummary({ model }: { model: SignalsModel | null }) {
  if (!model) return <p className="m-0 text-sm text-text-muted">Cargando…</p>
  if (model.sessions.length === 0) {
    return (
      <p className="m-0 text-sm text-text-muted">
        Todavía no jugaste ninguna sesión: entrá al VR con el Polar puesto y acá
        aparecen tus índices y la tendencia.
      </p>
    )
  }

  const { summary } = model
  const deltaPoints =
    summary.deltaVsPrevious !== null ? Math.round(summary.deltaVsPrevious * 100) : null

  if (summary.measuredCycles === 0) {
    return <NoSignalYet model={model} />
  }

  return (
    <div className="flex flex-col gap-md">
      <div className="grid grid-cols-2 gap-md lg:grid-cols-3 xl:grid-cols-6">
        <Kpi
          label="Índice última sesión"
          value={toIndex100(summary.lastMeanRCardio)?.toString() ?? '—'}
          hint={
            deltaPoints === null
              ? 'sobre 100'
              : `${deltaPoints >= 0 ? '+' : ''}${deltaPoints} vs. anterior`
          }
          tone={deltaPoints === null ? 'neutral' : deltaPoints >= 0 ? 'up' : 'down'}
          info={DASHBOARD_INFO.kpi_last_index}
        />
        <Kpi
          label="Mejor ciclo"
          value={toIndex100(summary.bestRCardio)?.toString() ?? '—'}
          hint="máximo histórico"
          info={DASHBOARD_INFO.kpi_best_cycle}
        />
        <Kpi
          label="Sesiones con señal"
          value={`${summary.sessionsWithSignal}`}
          hint={`de ${summary.totalSessions} jugadas`}
          info={DASHBOARD_INFO.kpi_sessions_with_signal}
        />
        <Kpi
          label="Ciclos medidos"
          value={`${summary.measuredCycles}`}
          hint={summary.totalCycles > 0 ? `de ${summary.totalCycles} detectados` : undefined}
          info={DASHBOARD_INFO.kpi_cycles_measured}
        />
        <Kpi
          label="HR pico promedio"
          value={summary.meanHrPeak !== null ? `${Math.round(summary.meanHrPeak)} bpm` : '—'}
          hint="durante el estímulo"
          info={DASHBOARD_INFO.kpi_hr_peak}
        />
        <Kpi
          label="Rebote HRV"
          value={
            summary.rmssdRecoveryRatio !== null
              ? `${summary.rmssdRecoveryRatio.toFixed(2)}×`
              : '—'
          }
          hint="recuperación / baseline"
          info={DASHBOARD_INFO.kpi_hrv_rebound}
        />
      </div>

      <div className="card border-primary/30">
        <div className="mb-md flex flex-wrap items-center justify-between gap-sm">
          <div className="flex items-center gap-xs">
            <h3 className="m-0">Tendencia por sesión</h3>
            <InfoTooltip label="Tendencia por sesión">
              {DASHBOARD_INFO.chart_trend}
            </InfoTooltip>
          </div>
          <span className="text-xs text-text-muted">
            {model.measuredSessions.length === 1
              ? 'una sola sesión con señal'
              : `${model.measuredSessions.length} sesiones con señal`}
          </span>
        </div>
        <SignalsTrendChart sessions={model.measuredSessions} />
      </div>
    </div>
  )
}

function Kpi({
  label,
  value,
  hint,
  tone = 'neutral',
  info,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'neutral' | 'up' | 'down'
  info?: string
}) {
  const hintClass =
    tone === 'up' ? 'text-goal' : tone === 'down' ? 'text-danger' : 'text-text-muted'
  return (
    <div className="card border-primary/30">
      <div className="flex items-center gap-xs text-xs uppercase tracking-wide text-text-muted">
        <span>{label}</span>
        {info && <InfoTooltip label={label}>{info}</InfoTooltip>}
      </div>
      <div className="mt-xs font-display text-xl font-bold text-primary">{value}</div>
      {hint && <div className={`mt-xs text-xs ${hintClass}`}>{hint}</div>}
    </div>
  )
}

function NoSignalYet({ model }: { model: SignalsModel }) {
  const { summary } = model
  return (
    <div className="card border-primary/40 bg-primary/10">
      <p className="m-0 text-sm text-text-secondary">
        Jugaste {summary.totalSessions}{' '}
        {summary.totalSessions === 1 ? 'sesión' : 'sesiones'} y ninguna produjo
        ciclos con datos suficientes.
      </p>
      <ul className="mt-sm flex flex-col gap-xs text-sm text-text-muted">
        {summary.sessionsWithoutSignal > 0 && (
          <li>
            {summary.sessionsWithoutSignal} sin sensores: hace falta la banda
            Polar H10 conectada.
          </li>
        )}
        {summary.sessionsProcessing > 0 && (
          <li>
            {summary.sessionsProcessing} todavía procesándose — esas aparecen
            solas.
          </li>
        )}
        <li>
          El índice necesita ~3 minutos de baseline con el Polar puesto antes de
          arrancar la sesión.
        </li>
      </ul>
    </div>
  )
}
